import React from 'react';
import { MapPin, Check } from 'lucide-react';
import { CHECKOUT_MESSAGES } from '../../constants/checkout';

export type ZipValidationStatus = 'idle' | 'valid' | 'invalid';

interface CheckoutZipCodeFieldProps {
  zipCode: string;
  onZipCodeChange: (value: string) => void;
  isValidating: boolean;
  validationStatus: ZipValidationStatus;
  title?: string;
  className?: string;
}

/** Campo único de CEP com validação via ViaCEP (sem demais campos de endereço na UI). */
const CheckoutZipCodeField: React.FC<CheckoutZipCodeFieldProps> = ({
  zipCode,
  onZipCodeChange,
  isValidating,
  validationStatus,
  title = CHECKOUT_MESSAGES.zipSection.title,
  className = '',
}) => {
  const { zipSection, validation } = CHECKOUT_MESSAGES;

  const borderClass =
    validationStatus === 'valid'
      ? 'border-green-500 focus:ring-green-500'
      : validationStatus === 'invalid'
        ? 'border-red-400 focus:ring-red-400'
        : 'border-gray-300 focus:ring-[#5C528A]';

  return (
    <div className={className}>
      <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <MapPin className="w-5 h-5" />
        {title}
      </h2>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">{zipSection.label} *</label>
        <div className="relative max-w-xs">
          <input
            type="text"
            inputMode="numeric"
            autoComplete="postal-code"
            required
            value={zipCode}
            onChange={(e) => onZipCodeChange(e.target.value)}
            placeholder="00000-000"
            aria-invalid={validationStatus === 'invalid'}
            aria-describedby="checkout-zip-hint"
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent placeholder:text-gray-400 ${borderClass}`}
          />
          {isValidating && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2" aria-hidden>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#5C528A]" />
            </div>
          )}
          {!isValidating && validationStatus === 'valid' && (
            <Check className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-green-600" aria-hidden />
          )}
        </div>
        <p id="checkout-zip-hint" className="text-xs text-gray-500 mt-1">
          {validation.zipHint}
        </p>
        {validationStatus === 'valid' && (
          <p className="text-xs text-green-700 mt-1 font-medium">{validation.zipValid}</p>
        )}
        {validationStatus === 'invalid' && (
          <p className="text-xs text-red-600 mt-1">{validation.zipInvalid}</p>
        )}
      </div>
    </div>
  );
};

export default CheckoutZipCodeField;
